/**
 * @fileoverview The order the share studio's photographs run in. A plant's photographs come from the server already in
 * the plant card's order (services/plants/gardenPlants: the plant's own pictures before a seed packet's, newest first
 * by the day taken, ties as the server read them), and the card draws them as given. The studio tells the story oldest
 * first (the direction growth reads in) and Pick lists it newest first, so a plant's story is the card's order turned
 * round and Pick turns it back: the same list as the card, ties included. Nothing here sorts a plant's photographs
 * (owner, 2026-11-03, E20: the sharer listed them in a different order from the card; the backend decides the order).
 *
 * Where: useShareStory (the story), ShareStepBands (Pick's grid turns the story back).
 * API: none; the photographs are the plants brief's, as the card has them.
 * Settings: none.
 * Words (en/es): none.
 *
 * @module shareOrder
 */

import type { PlantPhoto } from '@/shared/types/photo';

/**
 * A plant's photographs in the story's order: the card's order (as the server sent it) turned round, so the story runs
 * oldest first and Pick, which turns the story back, lists the card's own order.
 *
 * @param shareable - The plant's photographs in the card's order, those under review already left out.
 * @returns A new list, the card's order reversed.
 */
export function storyOrderOfCard(shareable: PlantPhoto[]): PlantPhoto[] {
  return [...shareable].reverse();
}

/**
 * The story's order for a plant or a season: a plant's is the card's, turned round (storyOrderOfCard); a season's
 * photographs (gathered across plants by the season page, so no card order) are put oldest first by the day taken, as
 * before; a bed's are as given.
 *
 * @param shareable - The photographs offered, those under review already left out, in the order they were given.
 * @param kind - What is shared: 'plant', 'location' or 'season'.
 * @returns A new list in the story's order.
 */
export function storyOrder(shareable: PlantPhoto[], kind: 'plant' | 'location' | 'season'): PlantPhoto[] {
  if (kind === 'plant') return storyOrderOfCard(shareable);
  if (kind === 'location') return [...shareable];
  return [...shareable].sort((a, b) => (a.date_taken || '').localeCompare(b.date_taken || ''));
}
